// Fixed useSmartDeviceDetection.ts - Optimized detection intervals without flickering
import { useState, useEffect, useCallback, useRef } from 'react';
import { getDeviceInfo, isPermissionDenied, getBrowserInfo } from '../../utils';

export enum DeviceState {
  NORMAL = 'normal',
  PERMISSION_DENIED = 'permission_denied',
  NO_DEVICE = 'no_device',
  DEVICE_IN_USE = 'device_in_use',
  DEVICE_UNPLUGGED = 'device_unplugged',
  SYSTEM_BLOCKED = 'system_blocked',
  UNKNOWN_ERROR = 'unknown_error',
}

export interface DeviceError {
  state: DeviceState;
  message: string;
  actionable: boolean;
  canOpenSettings: boolean;
}

// Track active streams globally to prevent conflicts
const activeStreams = new Map<string, MediaStream>();

// Cross-browser error mapping (keeping your existing implementation)
const mapErrorToDeviceState = (error: any, deviceType: 'camera' | 'microphone'): DeviceError => {
  const { isChrome, isFirefox, isSafari, isEdge } = getBrowserInfo();
  const errorName = error?.name || '';
  const errorMessage = error?.message || '';

  console.log(`🔍 Mapping error for ${deviceType}:`, {
    errorName,
    errorMessage,
    isChrome,
    isFirefox,
    isSafari,
    isEdge,
  });

  // Chrome-specific error handling
  if (isChrome) {
    switch (errorName) {
      case 'NotReadableError':
      case 'TrackStartError':
        return {
          state: DeviceState.SYSTEM_BLOCKED,
          message: `Your ${deviceType} is blocked by system settings. Click to open settings.`,
          actionable: true,
          canOpenSettings: true,
        };
      case 'NotAllowedError':
        if (errorMessage.includes('Permission denied')) {
          return {
            state: DeviceState.PERMISSION_DENIED,
            message: `${deviceType} access denied. Click to enable.`,
            actionable: true,
            canOpenSettings: true,
          };
        }
        return {
          state: DeviceState.SYSTEM_BLOCKED,
          message: `Your ${deviceType} is blocked. Click to open settings.`,
          actionable: true,
          canOpenSettings: true,
        };
      case 'NotFoundError':
        return {
          state: DeviceState.DEVICE_UNPLUGGED,
          message: `${deviceType} not found. Check connection.`,
          actionable: true,
          canOpenSettings: true,
        };
      case 'AbortError':
        return {
          state: DeviceState.DEVICE_IN_USE,
          message: `${deviceType} may be in use by another app.`,
          actionable: true,
          canOpenSettings: false,
        };
    }
  }

  // Firefox-specific error handling
  if (isFirefox) {
    switch (errorName) {
      case 'NotAllowedError':
        return {
          state: DeviceState.PERMISSION_DENIED,
          message: `${deviceType} access blocked. Click to enable.`,
          actionable: true,
          canOpenSettings: true,
        };
      case 'NotFoundError':
        return {
          state: DeviceState.NO_DEVICE,
          message: `No ${deviceType} found.`,
          actionable: true,
          canOpenSettings: false,
        };
      case 'NotReadableError':
        return {
          state: DeviceState.DEVICE_IN_USE,
          message: `${deviceType} is being used by another application.`,
          actionable: true,
          canOpenSettings: false,
        };
      case 'AbortError':
        if (errorMessage.includes('videoinput failed') || errorMessage.includes('audioinput failed')) {
          return {
            state: DeviceState.SYSTEM_BLOCKED,
            message: `${deviceType} blocked by Firefox or system settings. Click to fix.`,
            actionable: true,
            canOpenSettings: true,
          };
        }
        return {
          state: DeviceState.DEVICE_IN_USE,
          message: `${deviceType} may be in use by another application.`,
          actionable: true,
          canOpenSettings: false,
        };
      case 'InternalError':
        return {
          state: DeviceState.SYSTEM_BLOCKED,
          message: `${deviceType} blocked by system. Check privacy settings.`,
          actionable: true,
          canOpenSettings: true,
        };
    }
  }

  // Safari-specific error handling
  if (isSafari) {
    switch (errorName) {
      case 'NotAllowedError':
        return {
          state: DeviceState.PERMISSION_DENIED,
          message: `${deviceType} access denied. Click to enable in Safari.`,
          actionable: true,
          canOpenSettings: true,
        };
      case 'NotFoundError':
        return {
          state: DeviceState.NO_DEVICE,
          message: `No ${deviceType} available.`,
          actionable: true,
          canOpenSettings: false,
        };
      case 'NotReadableError':
      case 'OverconstrainedError':
        return {
          state: DeviceState.SYSTEM_BLOCKED,
          message: `${deviceType} blocked by system settings.`,
          actionable: true,
          canOpenSettings: true,
        };
      case 'TypeError':
        if (errorMessage.includes('constraints')) {
          return {
            state: DeviceState.DEVICE_UNPLUGGED,
            message: `${deviceType} may be disconnected.`,
            actionable: true,
            canOpenSettings: true,
          };
        }
        break;
    }
  }

  // Edge-specific error handling
  if (isEdge) {
    switch (errorName) {
      case 'NotAllowedError':
        return {
          state: DeviceState.PERMISSION_DENIED,
          message: `${deviceType} access denied. Click to enable.`,
          actionable: true,
          canOpenSettings: true,
        };
      case 'NotFoundError':
        return {
          state: DeviceState.DEVICE_UNPLUGGED,
          message: `${deviceType} not detected.`,
          actionable: true,
          canOpenSettings: true,
        };
      case 'NotReadableError':
        return {
          state: DeviceState.SYSTEM_BLOCKED,
          message: `${deviceType} blocked by Windows settings.`,
          actionable: true,
          canOpenSettings: true,
        };
    }
  }

  // Generic fallback
  if (errorName === 'NotAllowedError') {
    return {
      state: DeviceState.PERMISSION_DENIED,
      message: `${deviceType} access denied. Click to enable.`,
      actionable: true,
      canOpenSettings: true,
    };
  }

  return {
    state: DeviceState.UNKNOWN_ERROR,
    message: `${deviceType} error. Please refresh and try again.`,
    actionable: false,
    canOpenSettings: false,
  };
};

export function useSmartDeviceDetection() {
  const [audioError, setAudioError] = useState<DeviceError | null>(null);
  const [videoError, setVideoError] = useState<DeviceError | null>(null);
  const [isMonitoring, setIsMonitoring] = useState(false);

  // Track check in progress to prevent concurrent checks
  const checkInProgressRef = useRef<{ audio: boolean; video: boolean }>({ audio: false, video: false });
  const cleanupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastCheckTimeRef = useRef<{ audio: number; video: number }>({ audio: 0, video: 0 });

  // Cleanup helper for streams
  const cleanupStream = useCallback((streamKey: string) => {
    const stream = activeStreams.get(streamKey);
    if (stream) {
      try {
        stream.getTracks().forEach(track => {
          track.stop();
          console.log(`🧹 Stopped ${track.kind} track for ${streamKey}`);
        });
        activeStreams.delete(streamKey);
      } catch (e) {
        console.warn(`Error cleaning up stream ${streamKey}:`, e);
      }
    }
  }, []);

  // Enhanced device health check with proper cleanup
  const checkDeviceHealth = useCallback(
    async (deviceType: 'camera' | 'microphone', isPeriodicCheck: boolean = false): Promise<DeviceError | null> => {
      const { isSafari, isFirefox } = getBrowserInfo();
      const streamKey = `health-check-${deviceType}`;

      // Prevent concurrent checks for the same device
      if (checkInProgressRef.current[deviceType === 'camera' ? 'video' : 'audio']) {
        console.log(`⏳ Check already in progress for ${deviceType}, skipping...`);
        return null;
      }

      // For periodic checks, implement rate limiting
      if (isPeriodicCheck) {
        const now = Date.now();
        const lastCheck = lastCheckTimeRef.current[deviceType === 'camera' ? 'video' : 'audio'];
        const minInterval = deviceType === 'camera' ? 8000 : 5000; // 8s for video, 5s for audio

        if (now - lastCheck < minInterval) {
          console.log(`⏰ Skipping ${deviceType} check - too soon since last check`);
          return null;
        }

        lastCheckTimeRef.current[deviceType === 'camera' ? 'video' : 'audio'] = now;
      }

      // Clean up any existing stream for this check
      cleanupStream(streamKey);

      try {
        checkInProgressRef.current[deviceType === 'camera' ? 'video' : 'audio'] = true;

        // 1. Check permissions first (with browser-specific handling)
        const permissionName = deviceType === 'camera' ? 'camera' : 'microphone';

        if (!isSafari) {
          const isPermDenied = await isPermissionDenied(permissionName as PermissionName);
          if (isPermDenied) {
            return {
              state: DeviceState.PERMISSION_DENIED,
              message: `${deviceType} access denied. Click to enable.`,
              actionable: true,
              canOpenSettings: true,
            };
          }
        }

        // 2. Check device availability
        const { audioInputDevices, videoInputDevices } = await getDeviceInfo();
        const devices = deviceType === 'camera' ? videoInputDevices : audioInputDevices;

        if (devices.length === 0) {
          return {
            state: DeviceState.NO_DEVICE,
            message: `No ${deviceType} found. Check device connection.`,
            actionable: true,
            canOpenSettings: false,
          };
        }

        // 3. Test device access with minimal constraints
        try {
          let constraints: MediaStreamConstraints;

          if (deviceType === 'microphone') {
            constraints = { audio: true };
          } else {
            // Use minimal video constraints to reduce resource usage
            if (isSafari || isFirefox) {
              constraints = { video: true };
            } else {
              constraints = {
                video: {
                  width: { ideal: 320 },
                  height: { ideal: 240 },
                  frameRate: { ideal: 15 },
                },
              };
            }
          }

          const stream = await navigator.mediaDevices.getUserMedia(constraints);

          // Store stream for cleanup
          activeStreams.set(streamKey, stream);

          const tracks = deviceType === 'camera' ? stream.getVideoTracks() : stream.getAudioTracks();
          const track = tracks[0];

          if (track && track.readyState === 'live' && track.enabled) {
            // Clean up immediately after successful check
            cleanupStream(streamKey);
            return null; // Device is working
          } else {
            cleanupStream(streamKey);
            return {
              state: DeviceState.SYSTEM_BLOCKED,
              message: `Your ${deviceType} is blocked by system settings.`,
              actionable: true,
              canOpenSettings: true,
            };
          }
        } catch (streamError) {
          // Always clean up on error
          cleanupStream(streamKey);
          return mapErrorToDeviceState(streamError, deviceType);
        }
      } catch (error) {
        console.error(`❌ ${deviceType} health check error:`, error);
        cleanupStream(streamKey);
        return mapErrorToDeviceState(error, deviceType);
      } finally {
        checkInProgressRef.current[deviceType === 'camera' ? 'video' : 'audio'] = false;
      }
    },
    [cleanupStream]
  );

  // Cleanup all streams
  const cleanupAll = useCallback(() => {
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }

    // Clean up all active streams
    activeStreams.forEach((stream, key) => {
      try {
        stream.getTracks().forEach(track => track.stop());
        console.log(`🧹 Cleaned up stream: ${key}`);
      } catch (e) {
        console.warn(`Error cleaning up stream ${key}:`, e);
      }
    });
    activeStreams.clear();
  }, []);

  // Manual device check
  const recheckDevice = useCallback(
    async (deviceType: 'camera' | 'microphone') => {
      const error = await checkDeviceHealth(deviceType, false);
      if (deviceType === 'camera') {
        setVideoError(error);
      } else {
        setAudioError(error);
      }
    },
    [checkDeviceHealth]
  );

  // Clear errors
  const clearErrors = useCallback(() => {
    setAudioError(null);
    setVideoError(null);
  }, []);

  // Initial device check and monitoring setup
  useEffect(() => {
    setIsMonitoring(true);

    const checkAllDevices = async () => {
      try {
        // Add delay for Safari to initialize properly
        const { isSafari } = getBrowserInfo();
        if (isSafari) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }

        // Check both devices on initialization
        const [audioErr, videoErr] = await Promise.all([
          checkDeviceHealth('microphone', false),
          checkDeviceHealth('camera', false),
        ]);

        setAudioError(audioErr);
        setVideoError(videoErr);
      } catch (error) {
        console.warn('Device health check failed:', error);
      }
    };

    // Initial check
    checkAllDevices();

    // Listen for device changes
    const handleDeviceChange = () => {
      // Debounce device change events
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }
      cleanupTimeoutRef.current = setTimeout(checkAllDevices, 1000);
    };

    // Set up device change listener
    if (navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
      navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    }

    // Periodic health checks with optimized intervals
    const { isChrome } = getBrowserInfo();

    let audioCheckInterval: any = null;
    let videoCheckInterval: any = null;

    if (process.env.NODE_ENV !== 'test') {
      // Check audio frequently
      audioCheckInterval = setInterval(
        () => {
          checkDeviceHealth('microphone', true).then(error => {
            // Only update if changed to prevent flickering
            setAudioError(prevError => {
              if (JSON.stringify(prevError) !== JSON.stringify(error)) {
                console.log('🎤 Audio error state changed:', error);
                return error;
              }
              return prevError;
            });
          });
        },
        isChrome ? 5000 : 7000
      ); // 5s for Chrome, 7s for others

      // Check video with slightly longer interval to prevent freezing
      videoCheckInterval = setInterval(
        () => {
          checkDeviceHealth('camera', true).then(error => {
            // Only update if changed to prevent flickering
            setVideoError(prevError => {
              if (JSON.stringify(prevError) !== JSON.stringify(error)) {
                console.log('📷 Video error state changed:', error);
                return error;
              }
              return prevError;
            });
          });
        },
        isChrome ? 8000 : 10000
      ); // 8s for Chrome, 10s for others
    }

    return () => {
      if (navigator.mediaDevices && navigator.mediaDevices.removeEventListener) {
        navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
      }
      if (audioCheckInterval) clearInterval(audioCheckInterval);
      if (videoCheckInterval) clearInterval(videoCheckInterval);
      cleanupAll();
      setIsMonitoring(false);
    };
  }, [checkDeviceHealth, cleanupAll]);

  // Cleanup on unmount
  useEffect(() => {
    return cleanupAll;
  }, [cleanupAll]);

  return {
    audioError,
    videoError,
    recheckDevice,
    clearErrors,
    isMonitoring,
  };
}
